"use client";

import { useState, useEffect } from "react";
import { CatalogPageProps } from "./types";
import { CatalogFilters, CatalogFiltersState } from "./CatalogFilters";
import { CatalogGrid } from "./CatalogGrid";
import { CatalogPagination } from "./CatalogPagination";
import { Product } from "@/types/product.types";
import { QuickViewModal } from "@/components/shared/model/QuickViewModal";

import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbList,
} from "@/components/ui/breadcrumb";

import { useSearchProducts } from "@/features/products/hook/useSearchProducts";
import { useProducts, useNewArrivals } from "@/features/products/hook/useProducts";

import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
    SheetDescription,
    SheetHeader,
} from "@/components/ui/sheet";

import { Filter } from "lucide-react";

export const CatalogPage = ({
    title,
    variant = "all",
    background = "bg-primary-50",
    query,
    children,
}: CatalogPageProps) => {
    const [view, setView] = useState<"grid" | "list">("grid");
    const [gridSize, setGridSize] = useState<12 | 16>(12);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<CatalogFiltersState>({});

    const [selectedProduct, setSelectedProduct] =
        useState<Product | null>(null);

    const [isQuickViewOpen, setIsQuickViewOpen] =
        useState(false);

    /* ===============================
       Data Fetching Logic
    =============================== */
    const searchPayload = {
        q: query,
        page,
        limit: gridSize,
        inStock: true,
        ...filters
    };

    const productsParams = {
        page,
        limit: gridSize,
        sort: filters.sort as string | undefined
    };

    // Scroll to top on page change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [page]);

    // 1. Search Hook
    const searchRes = useSearchProducts(searchPayload, {
        enabled: variant === "search"
    });

    // 2. All Products Hook (Respecting backend GET /)
    const allRes = useProducts(productsParams, {
        enabled: variant === "all"
    });

    // 3. New Arrivals Hook
    const arrivalsRes = useNewArrivals({
        limit: gridSize
    }, {
        enabled: variant === "new-arrival"
    });

    /* ===============================
       Unified Data Source
    =============================== */
    const catalogData = variant === "search"
        ? searchRes.data
        : variant === "all"
            ? allRes.data
            : arrivalsRes.data;

    const products = catalogData?.products ?? [];
    const totalCount = catalogData?.meta?.total ?? 0;
    const totalPages = catalogData?.meta?.totalPages ?? 1;

    const loading = variant === "search"
        ? searchRes.isLoading
        : variant === "all"
            ? allRes.isLoading
            : arrivalsRes.isLoading;

    return (
        <section className={`relative overflow-hidden ${background}`}>
            <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">

                {/* Breadcrumb */}

                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink
                                href="/"
                                className="text-primary-500"
                            >
                                Accueil
                            </BreadcrumbLink>
                        </BreadcrumbItem>

                        <BreadcrumbSeparator />

                        <BreadcrumbItem>
                            <BreadcrumbPage className="text-primary-500">
                                {title}
                            </BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>

                {/* Title */}

                <h1 className="font-brastika text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] mt-6 mb-6 lg:mb-10">
                    {title}
                </h1>

                {/* Mobile Filters */}

                <div className="lg:hidden mb-6">

                    <Sheet>

                        <SheetTrigger asChild>

                            <button className="flex items-center gap-2 border border-primary-200 px-4 py-2 rounded-full text-sm">
                                <Filter size={16} />
                                Filtres
                            </button>

                        </SheetTrigger>

                        <SheetContent
                            side="left"
                            className="w-[85%] sm:w-95 overflow-y-auto overscroll-contain"
                        >

                            <SheetHeader>

                                <SheetTitle>Filtre</SheetTitle>

                                <SheetDescription>
                                    Filtrez vos produits
                                </SheetDescription>

                            </SheetHeader>

                            <CatalogFilters
                                filters={filters}
                                setFilters={setFilters}
                            />

                        </SheetContent>

                    </Sheet>

                </div>

                {/* Layout */}

                <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] xl:grid-cols-[320px_1fr] gap-6 lg:gap-10">

                    {/* Desktop Filters */}

                    <aside className="hidden lg:block self-start">
                        <CatalogFilters
                            filters={filters}
                            setFilters={setFilters}
                        />
                    </aside>

                    {/* Products */}

                    <div className="w-full">

                        <CatalogGrid
                            products={products}
                            count={totalCount}
                            isLoading={loading}
                            view={view}
                            onViewChange={setView}
                            gridSize={gridSize}
                            onGridSizeChange={(size) => {
                                setGridSize(size);
                                setPage(1);
                            }}
                            onQuickView={(p) => {
                                setSelectedProduct(p);
                                setIsQuickViewOpen(true);
                            }}
                            showViewToggle={variant !== "all"}
                            showGridSize={variant === "all"}
                        />

                    </div>

                </div>

                {/* Pagination */}

                {(variant === "all" || variant === "search") && !loading && (
                    <div className="w-full mt-8 flex justify-center">
                        <CatalogPagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </div>
                )}

                {/* Quick View */}

                <QuickViewModal
                    product={selectedProduct}
                    isOpen={isQuickViewOpen}
                    onClose={() => setIsQuickViewOpen(false)}
                />

            </div>
            {children && (
                <div className="absolute inset-0 pointer-events-none z-0">
                    {children}
                </div>
            )}
        </section>
    );
};